import React, { useEffect } from 'react';
import { Alert, Card, CardBody, Col, Row } from 'react-bootstrap';
import IFormStep from 'interfaces/IFormStep';
import ApplicationReviewBlock from '../Components/ApplicationReviewBlock';
import AccreditationPaymentModal from '../../AccreditationPayment/AccreditationPaymentModal';
import ModalButton from '../../../ModalButton';
import { useApplicationValidator } from '../hooks/useApplicationValidator';
import HelpButton from '../../../Form/HelpButton';

const SubmitAndPay: React.FC<IFormStep> = ({
    formData,
    handleFeedback,
    models,
    requirements,
    handleSubmitApplication,
    handleFetchApplication,
}) => {
    const [formErrors, setFormErrors] = React.useState<string[]>([]);

    const formValidator = useApplicationValidator(models.application);

    useEffect(() => {
        setFormErrors(formValidator.validateForm(formData));
    }, [formData]);

    const { application, updating } = models;

    const handlePaymentSuccess = () => {
        handleFeedback(
            'info',
            'Payment was successful. Forwarding application to ' +
                (application.isRenewal ? 'EMDR...' : 'supervisor(s)...')
        );
        handleSubmitApplication();
    };

    const handlePaymentError = () => {
        handleFeedback('danger', 'An error occurred during payment. Please try again later');
        handleFetchApplication();
    };

    const help = requirements.config?.help && requirements.config?.help['submitAndPay'];

    return (
        <>
            <Row className={'col-8'}>
                <h3 className={'fw-bold mb-3'}>
                    Review {help && <HelpButton title={'Submit & pay'} content={help} />}
                </h3>
                <h4>
                    Please take a moment to review your application carefully before proceeding to
                    ensure all information is accurate and complete.
                </h4>
            </Row>
            {formErrors.length > 0 && (
                <Row>
                    <Col xs={'auto'}>
                        <Alert variant={'warning'}>
                            <Alert.Heading>
                                Application contains errors. These must be fixed before you can
                                continue.
                            </Alert.Heading>
                            <ul>
                                {formErrors.map((err, i) => (
                                    <li key={`error-${i}`}>{err}</li>
                                ))}
                            </ul>
                        </Alert>
                    </Col>
                </Row>
            )}
            <Row className={'col-10 mb-3'}>
                <Card className={'border-0 bg-transparent'}>
                    <CardBody>
                        <ApplicationReviewBlock {...models} requirements={requirements} />
                    </CardBody>
                </Card>
            </Row>
            <Row className={'mt-2'}>
                <Col xs={6}>
                    {!formErrors.length &&
                        (application.isPaid ? (
                            <ModalButton
                                modalTitle={'Confirm application submission'}
                                confirmBtnText={'Submit for review'}
                                onConfirmModalBtn={handleSubmitApplication}
                                buttonText={'Submit for review'}
                                buttonProps={{ disabled: updating }}
                            >
                                <p>
                                    By clicking below you are confirming that your application is
                                    accurate and ready for evaluation.
                                </p>
                            </ModalButton>
                        ) : (
                            <AccreditationPaymentModal
                                application={application}
                                onPaymentSuccess={handlePaymentSuccess}
                                onPaymentError={handlePaymentError}
                            />
                        ))}
                </Col>
            </Row>
        </>
    );
};

export default SubmitAndPay;
